import React, { useState, useCallback } from 'react';
import {
  Stethoscope, PawPrint, Smile, ShoppingBag, Wrench,
  Home, Scissors, Dumbbell, Car, GraduationCap,
  Scale, Building2, CheckSquare, PenLine,
  ChevronDown, HelpCircle
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Data definitions
// ─────────────────────────────────────────────────────────────────────────────

export interface BusinessSector {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;          // bg color class for selected state
  borderColor: string;    // border class
  useCases: string[];     // 3–5 opciones de funcionalidades
}

const BUSINESS_SECTORS: BusinessSector[] = [
  {
    id: 'medicina',
    label: 'Medicina',
    icon: <Stethoscope className="w-5 h-5" />,
    color: 'bg-blue-50',
    borderColor: 'border-blue-400',
    useCases: [
      'Recordatorio de citas',
      'Seguimiento post-consulta',
      'Renovación de tratamientos',
      'Cobros y pagos pendientes',
      'Campañas de prevención',
    ],
  },
  {
    id: 'veterinaria',
    label: 'Veterinaria',
    icon: <PawPrint className="w-5 h-5" />,
    color: 'bg-emerald-50',
    borderColor: 'border-emerald-400',
    useCases: [
      'Recordatorio de vacunas',
      'Citas de control',
      'Seguimiento de mascotas',
      'Cobros y pagos',
      'Promociones y descuentos',
    ],
  },
  {
    id: 'odontologia',
    label: 'Odontología',
    icon: <Smile className="w-5 h-5" />,
    color: 'bg-cyan-50',
    borderColor: 'border-cyan-400',
    useCases: [
      'Recordatorio de limpieza dental',
      'Control de ortodoncia',
      'Cobros de tratamientos',
      'Seguimiento post-tratamiento',
      'Captación de nuevos pacientes',
    ],
  },
  {
    id: 'cobros',
    label: 'Cobros',
    icon: <ShoppingBag className="w-5 h-5" />,
    color: 'bg-yellow-50',
    borderColor: 'border-yellow-400',
    useCases: [
      'Recordatorio de pagos vencidos',
      'Notificaciones de facturación',
      'Acuerdos de pago',
      'Confirmaciones de recibo',
      'Campañas de recuperación de cartera',
    ],
  },
  {
    id: 'servicios_tecnicos',
    label: 'Servicios Técnicos',
    icon: <Wrench className="w-5 h-5" />,
    color: 'bg-orange-50',
    borderColor: 'border-orange-400',
    useCases: [
      'Actualización de estado del servicio',
      'Recordatorio de mantenimiento',
      'Cobros y confirmaciones',
      'Encuestas de satisfacción',
      'Renovación de contratos',
    ],
  },
  {
    id: 'inmobiliaria',
    label: 'Inmobiliaria',
    icon: <Home className="w-5 h-5" />,
    color: 'bg-purple-50',
    borderColor: 'border-purple-400',
    useCases: [
      'Recordatorio de pagos de arriendo',
      'Notificaciones de visitas',
      'Seguimiento de prospectos',
      'Cobros de administración',
      'Renovación de contratos',
    ],
  },
  {
    id: 'salon_belleza',
    label: 'Salón de Belleza',
    icon: <Scissors className="w-5 h-5" />,
    color: 'bg-pink-50',
    borderColor: 'border-pink-400',
    useCases: [
      'Confirmación de citas',
      'Recordatorio de servicios recurrentes',
      'Cobros y pagos',
      'Promociones y ofertas',
      'Fidelización de clientes',
    ],
  },
  {
    id: 'fitness',
    label: 'Fitness / Gym',
    icon: <Dumbbell className="w-5 h-5" />,
    color: 'bg-red-50',
    borderColor: 'border-red-400',
    useCases: [
      'Renovación de membresías',
      'Recordatorio de clases',
      'Cobros y pagos',
      'Rutinas personalizadas',
      'Campañas de captación',
    ],
  },
  {
    id: 'transporte',
    label: 'Transporte',
    icon: <Car className="w-5 h-5" />,
    color: 'bg-slate-50',
    borderColor: 'border-slate-400',
    useCases: [
      'Confirmación de viajes',
      'Recordatorio de mantenimiento',
      'Cobros y pagos',
      'Seguimiento de entregas',
      'Notificaciones de rutas',
    ],
  },
  {
    id: 'educacion',
    label: 'Educación',
    icon: <GraduationCap className="w-5 h-5" />,
    color: 'bg-indigo-50',
    borderColor: 'border-indigo-400',
    useCases: [
      'Recordatorio de pagos de matrícula',
      'Notificaciones de clases',
      'Seguimiento académico',
      'Campañas de inscripción',
      'Renovación de cursos',
    ],
  },
  {
    id: 'juridico',
    label: 'Jurídico / Legal',
    icon: <Scale className="w-5 h-5" />,
    color: 'bg-amber-50',
    borderColor: 'border-amber-400',
    useCases: [
      'Recordatorio de citas',
      'Seguimiento de casos',
      'Cobros de honorarios',
      'Notificaciones de vencimientos',
      'Documentación pendiente',
    ],
  },
  {
    id: 'empresarial',
    label: 'Empresarial / B2B',
    icon: <Building2 className="w-5 h-5" />,
    color: 'bg-teal-50',
    borderColor: 'border-teal-400',
    useCases: [
      'Gestión de cartera',
      'Seguimiento de propuestas',
      'Cobros y facturación',
      'Renovación de contratos',
      'Campañas de fidelización',
    ],
  },
];

// ID especial para el sector "Otro"
const SECTOR_OTHER_ID = '__sector_otro__';


// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface SectorTagProps {
  sector: BusinessSector;
  selected: boolean;
  onSelect: (id: string) => void;
}

const SectorTag: React.FC<SectorTagProps> = ({ sector, selected, onSelect }) => (
  <button
    type="button"
    id={`sector-${sector.id}`}
    onClick={() => onSelect(sector.id)}
    className={`
      group flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-sm font-medium
      transition-all duration-200 cursor-pointer
      ${selected
        ? `${sector.color} ${sector.borderColor} text-gray-800 shadow-sm scale-[1.02]`
        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
      }
    `}
  >
    <span className={`transition-colors ${selected ? 'text-gray-700' : 'text-gray-400 group-hover:text-gray-500'}`}>
      {sector.icon}
    </span>
    {sector.label}
    {selected && (
      <span className="ml-auto w-4 h-4 rounded-full bg-[#1CB3B3] flex items-center justify-center flex-shrink-0">
        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </span>
    )}
  </button>
);

interface UseCaseCheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const UseCaseCheckbox: React.FC<UseCaseCheckboxProps> = ({ label, checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`
      w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-sm text-left
      transition-all duration-150 cursor-pointer group
      ${checked
        ? 'border-[#1CB3B3] bg-[#E8F7F7] text-gray-800'
        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
      }
    `}
  >
    <span className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
      ${checked ? 'bg-[#1CB3B3] border-[#1CB3B3]' : 'border-gray-300 group-hover:border-gray-400'}`}
    >
      {checked && (
        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </span>
    <span className="flex-1">{label}</span>
    <CheckSquare className={`w-4 h-4 flex-shrink-0 transition-opacity ${checked ? 'text-[#1CB3B3] opacity-100' : 'opacity-0'}`} />
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export interface BusinessProfileValue {
  business_sector: string;    // ID del sector seleccionado (o '__sector_otro__')
  custom_sector?: string;     // Texto libre cuando sector = '__sector_otro__'
  use_cases: string[];
  custom_use_case?: string;
}

export interface BusinessProfileSelectorProps {
  value: BusinessProfileValue;
  onChange: (value: BusinessProfileValue) => void;
  errors?: {
    business_sector?: string;
    custom_sector?: string;
    use_cases?: string;
    custom_use_case?: string;
  };
  disabled?: boolean;
}

export type BusinessProfileErrors = NonNullable<BusinessProfileSelectorProps['errors']>;

export const EMPTY_BUSINESS_PROFILE: BusinessProfileValue = {
  business_sector: '',
  custom_sector: undefined,
  use_cases: [],
  custom_use_case: undefined,
};

/** Returns an empty object when the profile is complete. */
export function validateBusinessProfile(value: BusinessProfileValue): BusinessProfileErrors {
  const errors: BusinessProfileErrors = {};
  const isOther = value.business_sector === SECTOR_OTHER_ID;

  if (!value.business_sector) {
    errors.business_sector = 'Selecciona el sector de tu negocio';
  }
  if (isOther && !value.custom_sector?.trim()) {
    errors.custom_sector = 'Por favor describe tu sector';
  }
  const hasUses = (value.use_cases?.length ?? 0) > 0 || Boolean(value.custom_use_case?.trim());
  if (value.business_sector && !hasUses) {
    errors.use_cases = 'Selecciona al menos un uso o describe el tuyo';
  }
  if (isOther && !value.custom_use_case?.trim()) {
    errors.custom_use_case = 'Por favor describe la funcionalidad que necesitas';
  }
  return errors;
}

/** Sector to store: the free text when the user picked "Otro sector". */
export function resolveBusinessSector(value: BusinessProfileValue): string {
  return value.business_sector === SECTOR_OTHER_ID
    ? (value.custom_sector?.trim() || 'Otro')
    : value.business_sector;
}

const OTHER_ID = '__otro__';

const BusinessProfileSelector: React.FC<BusinessProfileSelectorProps> = ({
  value,
  onChange,
  errors,
  disabled,
}) => {
  const [showOtherInput, setShowOtherInput] = useState(false);

  // El sector puede ser uno de los predefinidos o '__sector_otro__'
  const isOtherSector = value.business_sector === SECTOR_OTHER_ID;
  const selectedSector = isOtherSector
    ? null
    : BUSINESS_SECTORS.find(s => s.id === value.business_sector);

  const handleSectorSelect = useCallback(
    (id: string) => {
      if (disabled) return;
      // Reset use_cases when changing sector
      onChange({
        ...value,
        business_sector: id,
        custom_sector: id === SECTOR_OTHER_ID ? (value.custom_sector ?? '') : undefined,
        use_cases: [],
        custom_use_case: undefined,
      });
      setShowOtherInput(false);
    },
    [value, onChange, disabled]
  );

  const handleUseCaseToggle = useCallback(
    (useCase: string, checked: boolean) => {
      if (disabled) return;
      const next = checked
        ? [...(value.use_cases || []), useCase]
        : (value.use_cases || []).filter(u => u !== useCase);
      onChange({ ...value, use_cases: next });
    },
    [value, onChange, disabled]
  );

  const handleOtherToggle = useCallback(() => {
    if (disabled) return;
    const isCurrentlyShown = showOtherInput;
    setShowOtherInput(!isCurrentlyShown);
    if (isCurrentlyShown) {
      // Hiding "other" → clear custom value
      onChange({ ...value, custom_use_case: undefined });
    }
  }, [showOtherInput, value, onChange, disabled]);

  return (
    <div className="space-y-6">
      {/* ── Sector selection ─────────────────────────────────────────── */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">
          ¿A qué sector pertenece tu negocio? *
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Selecciona el que mejor describe tu actividad
        </p>

        <div className="flex flex-wrap gap-2">
          {BUSINESS_SECTORS.map(sector => (
            <SectorTag
              key={sector.id}
              sector={sector}
              selected={value.business_sector === sector.id}
              onSelect={handleSectorSelect}
            />
          ))}

          {/* ── Otro sector ─────────────────────────────────────────── */}
          <button
            type="button"
            id="sector-otro"
            onClick={() => handleSectorSelect(SECTOR_OTHER_ID)}
            disabled={disabled}
            className={`
              group flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-sm font-medium
              transition-all duration-200 cursor-pointer
              ${isOtherSector
                ? 'bg-violet-50 border-violet-400 text-gray-800 shadow-sm scale-[1.02]'
                : 'bg-white border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:bg-gray-50'
              }
            `}
          >
            <HelpCircle className={`w-5 h-5 transition-colors ${isOtherSector ? 'text-violet-500' : 'text-gray-400 group-hover:text-gray-500'}`} />
            Otro
            {isOtherSector && (
              <span className="ml-auto w-4 h-4 rounded-full bg-[#1CB3B3] flex items-center justify-center flex-shrink-0">
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </span>
            )}
          </button>
        </div>

        {/* Input expandible cuando sector = "Otro" */}
        {isOtherSector && (
          <div className="mt-3 animate-in slide-in-from-top-1 duration-200">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              ¿A qué sector pertenece tu negocio? *
            </label>
            <input
              id="custom-sector-input"
              type="text"
              value={value.custom_sector ?? ''}
              onChange={e => onChange({ ...value, custom_sector: e.target.value })}
              placeholder="Ej: Agencia de viajes, Fotografía, Estética..."
              disabled={disabled}
              autoFocus
              className={`
                w-full px-4 py-3 text-sm border-2 rounded-xl
                focus:outline-none transition-all duration-200
                ${errors?.custom_sector
                  ? 'border-red-300 bg-red-50'
                  : 'border-violet-200 focus:border-violet-400 focus:bg-violet-50/30'
                }
              `}
            />
            {errors?.custom_sector && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <span>⚠</span> {errors.custom_sector}
              </p>
            )}
          </div>
        )}

        {errors?.business_sector && (
          <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
            <span>⚠</span> {errors.business_sector}
          </p>
        )}
      </div>

      {/* ── Use cases (shown after sector selection) ─────────────── */}
      {(selectedSector || isOtherSector) && (
        <div
          className="animate-in slide-in-from-top-2 duration-300"
          style={{ animationFillMode: 'both' }}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[#1CB3B3]">
              {selectedSector ? selectedSector.icon : <PenLine className="w-5 h-5" />}
            </span>
            <label className="block text-sm font-semibold text-gray-700">
              ¿Para qué usarás Reveca{selectedSector ? ` en tu ${selectedSector.label}` : ''}? *
            </label>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            {selectedSector 
              ? 'Puedes seleccionar todas las que apliquen'
              : 'Describe la funcionalidad o uso principal que buscas'}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {selectedSector && selectedSector.useCases.map(useCase => (
              <UseCaseCheckbox
                key={useCase}
                label={useCase}
                checked={(value.use_cases || []).includes(useCase)}
                onChange={checked => handleUseCaseToggle(useCase, checked)}
              />
            ))}

            {/* "Otro" toggle o Input directo si es sector "Otro" */}
            {(selectedSector || isOtherSector) && (
              <div className="sm:col-span-2">
                {selectedSector && (
                  <button
                    type="button"
                    id="usecase-otro"
                    onClick={handleOtherToggle}
                    disabled={disabled}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-sm text-left
                      transition-all duration-150 cursor-pointer group
                      ${showOtherInput
                        ? 'border-[#1F4068] bg-[#1F4068]/5 text-gray-800'
                        : 'border-dashed border-gray-300 bg-white text-gray-500 hover:border-gray-400 hover:bg-gray-50'
                      }
                    `}
                  >
                    <span className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
                      ${showOtherInput ? 'bg-[#1F4068] border-[#1F4068]' : 'border-gray-300 group-hover:border-gray-400'}`}
                    >
                      {showOtherInput && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <PenLine className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 font-medium">Otro uso personalizado</span>
                    <ChevronDown
                      className={`w-4 h-4 flex-shrink-0 transition-transform ${showOtherInput ? 'rotate-180' : ''}`}
                    />
                  </button>
                )}

                {/* Si es sector "Otro", el input de uso es SIEMPRE visible y obligatorio */}
                {(showOtherInput || isOtherSector) && (
                  <div className={`pl-2 pr-2 pb-1 animate-in slide-in-from-top-1 duration-200 mt-2 ${isOtherSector ? '!mt-0' : ''}`}>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">
                      {isOtherSector ? '¿Qué funcionalidad o uso necesitas?' : 'Descríbelo brevemente'} *
                    </label>
                    <textarea
                      id="custom-use-case-input"
                      value={value.custom_use_case || ''}
                      onChange={e => onChange({ ...value, custom_use_case: e.target.value })}
                      placeholder={isOtherSector ? "Ej: Enviar cobros automáticos a mis clientes de consultoría..." : "Ej: Notificaciones de turno para empleados..."}
                      rows={2}
                      disabled={disabled}
                      className={`
                        w-full px-3 py-2.5 text-sm border-2 rounded-xl resize-none
                        focus:outline-none transition-all duration-200
                        ${errors?.custom_use_case
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 focus:border-[#1F4068] focus:bg-[#1F4068]/5'
                        }
                      `}
                    />
                    {errors?.custom_use_case && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <span>⚠</span> {errors.custom_use_case}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {errors?.use_cases && (
            <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
              <span>⚠</span> {errors.use_cases}
            </p>
          )}

          {/* Selection summary */}
          {((value.use_cases?.length ?? 0) > 0 || value.custom_use_case) && (
            <div className="mt-3 px-4 py-2.5 bg-gradient-to-r from-[#E8F7F7] to-indigo-50 rounded-xl border border-[#1CB3B3]/30">
              <p className="text-xs font-medium text-[#0D7377] mb-1">
                ✓ Seleccionado{(value.use_cases?.length ?? 0) + (value.custom_use_case ? 1 : 0) > 1 ? 's' : ''}:{' '}
                {(value.use_cases?.length ?? 0) + (value.custom_use_case ? 1 : 0)} uso
                {(value.use_cases?.length ?? 0) + (value.custom_use_case ? 1 : 0) > 1 ? 's' : ''}
              </p>
              <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
                {[...(value.use_cases || []), value.custom_use_case ? `Otro: ${value.custom_use_case}` : '']
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessProfileSelector;
